import React, { useEffect } from 'react';
import MetaHeader from 'components/MetaHeader';
import Header from 'components/Header';
import styled from 'styled-components';
import { breakpoints } from 'constant/style';
import { ArrowBackIcon } from 'assets/svg';
import { getMicrositeBySlug, getTncBySlug, getTncLanguagesBySlug } from 'services';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import PoweredByMomos from 'components/PoweredByMomos';
import { ETncKind, ITnc } from 'utils/types';
import { GetServerSideProps } from 'next';
import { RightOutlined, GlobalOutlined } from '@ant-design/icons';
import { find, filter, includes, toLower, isEmpty } from 'lodash';
import TextInput from '../../../components/TextInput';
import { useRouter } from 'next/router';
import { TERMS_AND_CONDITIONS_URL, LANGUAGE_LABELS } from 'constant';
import Select from 'components/Select';

interface Props {
  slug: string;
  tncList: ITnc[];
  brandDetails: any;
  currentLanguage: string;
  availableLanguages: string[];
}

export default function Tnc({ tncList, brandDetails, currentLanguage, availableLanguages }: Props) {
  const languageOptions = availableLanguages.map((lang) => ({
    id: lang,
    label: LANGUAGE_LABELS[lang] || lang,
  }));
  const [showText, setShowText] = React.useState(false);
  const [text, setText] = React.useState('');
  const [filterTncList, setFilterTncList] = React.useState(tncList);
  const router = useRouter();

  const handleLanguageChange = (language: string) => {
    router.push({
      pathname: router.pathname,
      query: { ...router.query, language },
    });
  };

  useEffect(() => {
    // Your condition here
    const shouldRedirect = isEmpty(tncList); // Replace with your actual condition
    if (shouldRedirect) {
      // Redirect to another website
      router.push(TERMS_AND_CONDITIONS_URL);
      return;
    }
  }, [tncList]); // Empty dependency array ensures it runs once on page load

  const handleClick = (brandId: string, kind?: ETncKind) => {
    ga.event(TRACKING.TNC.BUTTON.VIEW_DETAIL);
    setShowText(false);
    setText('');
    if (brandId === 'base') {
      window.open(TERMS_AND_CONDITIONS_URL, '_blank', 'noreferrer');
      return;
    }
    const tnc = find(tncList, { brandId, kind });
    switch (tnc?.type) {
      case 'LINK':
        if (tnc.thirdPartyLink) window.open(tnc.thirdPartyLink, '_blank', 'noreferrer');
        break;
      case 'FILE':
        if (tnc.fileUrl) window.open(tnc.fileUrl, '_blank', 'noreferrer');
        break;
      case 'TEXT':
        setShowText(true);
        setText(tnc.content ?? '');
        break;
    }
  };
  const handleBack = () => {
    setText('');
    setShowText(false);
  };
  const onChangeSearch = (e: any) => {
    const val = e.target.value;
    const filtered = filter(tncList, (i) => {
      return includes(toLower(i.brandName), toLower(val));
    });
    setFilterTncList(filtered);
  };
  return (
    <PageWrap>
      <MetaHeader
        title="Terms and Conditions"
        image={brandDetails?.logoUrl ? brandDetails?.logoUrl : undefined}
        pixelId={brandDetails?.micrositePixelId}
      />
      <Header
        brandDetails={brandDetails}
        hideMenuLink
        fixedHeader={false}
        theme={brandDetails?.micrositeConfigDetails?.micrositeTheme}
      />
      <Container className="mr-20 ml-20">
        <PageTitle className="text-center">
          <h3>Terms and Conditions</h3>
        </PageTitle>
        {showText && (
          <div className="text-center">
            <h2>{text}</h2>
          </div>
        )}
        {!showText && !isEmpty(tncList) && (
          <TextInput placeholder="Search" onChange={onChangeSearch} className="mb-20" />
        )}
        {!showText && (
          <div className="tnc-wrap" onClick={() => handleClick('base')} key="base">
            Software Provider Terms and Conditions <RightOutlined className="float-right mt-5" />{' '}
          </div>
        )}
        {!showText && (
          <div className=" justify-content-center">
            {filterTncList?.map((tnc) => {
              return (
                <div
                  className="tnc-wrap"
                  onClick={() => handleClick(tnc.brandId, tnc.kind)}
                  key={`${tnc.brandId}-${tnc.kind}`}
                >
                  {tnc.brandName} {tnc.kind === ETncKind.PRIVACY_POLICY ? 'Privacy Policy' : 'Terms and Condition'}{' '}
                  <RightOutlined className="float-right mt-5" />{' '}
                </div>
              );
            })}
          </div>
        )}

        {showText && (
          <div className="back-button-wrap mt-50 mb-20" onClick={handleBack}>
            <ArrowBackIcon className="mr-15" />
            <div className="body-text back-title">Back</div>
          </div>
        )}

        <PoweredByMomos className="powered-custom-style" />
      </Container>

      {languageOptions.length > 1 && (
        <LanguageDropdownWrapper>
          <GlobalOutlined style={{ marginRight: 8 }} />
          <Select
            value={currentLanguage}
            options={languageOptions}
            onChange={handleLanguageChange}
            style={{ width: 160 }}
          />
        </LanguageDropdownWrapper>
      )}
    </PageWrap>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, req, query }) => {
  // Fetch available languages for this slug
  let availableLanguages: string[] = ['en'];
  try {
    const { data: languages = [] } = await getTncLanguagesBySlug(req, params?.slug);
    if (languages.length > 0) {
      availableLanguages = languages;
    }
  } catch (error) {
    console.error('Failed to fetch available languages:', error);
  }

  // Use query param if provided, otherwise extract from Accept-Language header
  let language = query.language as string | undefined;

  if (!language) {
    const acceptLanguage = req.headers['accept-language'] || 'en';
    const browserLang = acceptLanguage.split(',')[0].split('-')[0] || 'en';
    // Use browser language if available, otherwise default to first available language
    language = availableLanguages.includes(browserLang) ? browserLang : availableLanguages[0];
  }

  const { data: tncList = [] } = await getTncBySlug(req, params?.slug, language);
  const { data: brandDetails } = await getMicrositeBySlug(req, params?.slug);

  return { props: { slug: params?.slug, tncList, brandDetails, currentLanguage: language, availableLanguages } };
};

const PageWrap = styled.div`
  margin-bottom: 50px;

  @media (min-width: ${breakpoints.tablet}) {
    margin-bottom: 0px;
  }
`;

const LanguageDropdownWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  background: white;
  padding: 8px 12px;
  border-radius: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;

  .momos-select {
    width: auto;
  }

  .ant-select-selector {
    min-height: 36px !important;
    border: none !important;
    background: transparent !important;
  }

  @media (max-width: ${breakpoints.tablet}) {
    bottom: 80px;
  }
`;

const PageTitle = styled.div`
  padding: 24px;

  h1 {
    font-size: 36px;
    line-height: 40px;
    text-transform: capitalize;
    font-family: var(--font-family-bold);
    margin-bottom: 0px;
  }
`;

const Container = styled.div`
  .menu-list-wrap {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 30px;
    }
  }

  .scrollable-sections {
    position: relative;
  }

  .sections-wrap {
    padding-top: 30px;
    padding-bottom: 15px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .category-select-desk {
    display: block;

    position: sticky;
    z-index: 1;
    background: #fff;
    top: 0px;
    transition: unset;

    .ant-tabs-tab {
      font-weight: bold;
      font-size: 16px;
      color: var(--grey-dark);
      padding: 12px 0px 12px 0px;
      margin: 0px;
      margin-right: 24px;
    }

    .ant-tabs-extra-content {
      .tab-action-button {
        margin: 0px;

        &:first-child {
          margin-right: 20px;
        }
      }
    }

    .ant-tabs-nav {
      padding: 0px 24px;
    }

    .ant-tabs-tab-btn:active,
    .ant-tabs-tab-btn:focus {
      color: var(--grey-dark) !important;
    }

    .ant-tabs-tab ant-tabs-tab-active {
      border-bottom-color: var(--orange);
    }

    .ant-tabs-ink-bar {
      background: var(--orange);
      height: 4px;
    }

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--grey-dark);
    }
  }

  .category-select-res {
    display: block;
    margin: 30px 0px;
    @media (min-width: ${breakpoints.tablet}) {
      display: none;
    }
  }

  .powered-custom-style {
    padding-bottom: 80px;
  }
`;
